import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { LocalizationProvider } from 'ilenia';
import { useModal } from 'react-context-modals';
import { ModalProvider } from 'react-context-modals';
import styled, { ThemeProvider } from 'styled-components';

import { projectTheme } from '@bscom/styling/old-brand-theme';

const ModalClose = (props) => (
  <svg
    {...props}
    width="21"
    height="21"
    fill="none"
    viewBox="0 0 21 21"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#1A66FF"
      d="M17.819 0L10.5 7.319 3.181 0 0 3.181 7.318 10.5 0 17.819 3.181 21l7.319-7.319L17.819 21 21 17.819 13.682 10.5 21 3.181 17.819 0z"
    />
  </svg>
);

const StyledModalClose = styled(ModalClose)`
  top: 0;
  right: 0;
  cursor: pointer;
  position: absolute;
  margin: 30px 30px 0 0;
`;

const RouterEventRegisters = ({ localeCode }) => {
  const router = useRouter();
  const { hideModal } = useModal();

  useEffect(() => {
    const handleRouteChange = () => {
      window?.analytics?.page({
        locale: localeCode,
      });

      hideModal();
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => router.events.off('routeChangeComplete', handleRouteChange);
  }, [router]);

  return null;
};

export const BusinessSiteProvider = ({
  children,
  localeCode,
  translations,
}) => {
  return (
    <ThemeProvider theme={projectTheme}>
      <LocalizationProvider locale={localeCode} translations={translations}>
        <ModalProvider animated CloseComponent={StyledModalClose}>
          <>
            <RouterEventRegisters localeCode={localeCode} />
            {children}
          </>
        </ModalProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

const fallbackLocalesConfig = [
  {
    code: 'de-AT',
    fallbackCode: 'de-DE',
  },
  {
    code: 'de-CH',
    fallbackCode: 'de-DE',
  },
];

export const buildTranslations = async (localeCode) => {
  let translations = {};

  try {
    const localisedLibTranslations = await import(
      `@trustpilot/businesssite-components/localisation/${localeCode}/strings.json`
    );

    const localisedTranslations = await import(
      `../localisation/${localeCode}/strings.json`
    );

    translations = {
      ...localisedLibTranslations,
      ...localisedTranslations,
    };
  } catch {
    try {
      const fallbackLocale = fallbackLocalesConfig.find(
        (locale) => locale.code.toLowerCase() === localeCode.toLowerCase()
      );

      if (!fallbackLocale) {
        throw 'fallback is not defined';
      }

      const fallbackLibTranslations = await import(
        `@trustpilot/businesssite-components/localisation/${fallbackLocale.fallbackCode}/strings.json`
      );

      const fallbackTranslations = await import(
        `../localisation/${fallbackLocale.fallbackCode}/strings.json`
      );

      translations = {
        ...fallbackLibTranslations,
        ...fallbackTranslations,
      };
    } catch (e) {
      const enLibTranslations = await import(
        `@trustpilot/businesssite-components/localisation/en/strings.json`
      );
      const enTranslations = await import('../localisation/en/strings.json');

      translations = {
        ...enLibTranslations,
        ...enTranslations,
      };
    }
  }

  return translations;
};
